'use client';

import type { PaymentProviderId } from '@/payment/provider-list';
import {
  type CheckoutErrorCode,
  checkoutErrorCodes,
} from '@/payment/services/checkout/types';
import { useEffect, useState } from 'react';

type ProviderOptionsByIntentKey = Record<string, readonly PaymentProviderId[]>;

type ProviderOptionsMapState = {
  errorCode: CheckoutErrorCode | null;
  pending: boolean;
  providerOptionsByIntentKey: ProviderOptionsByIntentKey;
};

type ProviderOptionsMapResponse = {
  errorCode: CheckoutErrorCode | null;
  providerOptionsByIntentKey: ProviderOptionsByIntentKey;
};

const providerOptionsRequestCache = new Map<
  string,
  Promise<ProviderOptionsMapResponse>
>();

const EMPTY_PROVIDER_OPTIONS_MAP: ProviderOptionsByIntentKey = {};

function isCheckoutErrorCode(value: unknown): value is CheckoutErrorCode {
  return (
    typeof value === 'string' &&
    (checkoutErrorCodes as readonly string[]).includes(value)
  );
}

function normalizeCheckoutProviderOptionsErrorCode(value: unknown) {
  return isCheckoutErrorCode(value) ? value : 'provider_unavailable';
}

function isPaymentProviderId(value: unknown): value is PaymentProviderId {
  return value === 'stripe' || value === 'paypal';
}

function normalizeCheckoutIntentKeys(checkoutIntentKeys: readonly string[]) {
  return Array.from(
    new Set(
      checkoutIntentKeys
        .map((checkoutIntentKey) => checkoutIntentKey.trim())
        .filter(Boolean)
    )
  ).sort();
}

function getRequestCacheKey(params: {
  checkoutIntentKeys: readonly string[];
  locale: string;
}) {
  return `${params.locale}:${params.checkoutIntentKeys.join('|')}`;
}

function parseProviderOptionsByIntentKey(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_PROVIDER_OPTIONS_MAP;
  }

  return Object.fromEntries(
    Object.entries(value).map(([checkoutIntentKey, providerOptions]) => [
      checkoutIntentKey,
      Array.isArray(providerOptions)
        ? providerOptions.filter(isPaymentProviderId)
        : [],
    ])
  );
}

function loadProviderOptionsMap(params: {
  checkoutIntentKeys: readonly string[];
  locale: string;
}) {
  const cacheKey = getRequestCacheKey(params);
  const cached = providerOptionsRequestCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const request = fetch('/api/payment/checkout/admin-v2-runtime/options', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      checkoutIntentKeys: params.checkoutIntentKeys,
      locale: params.locale,
    }),
  })
    .then(async (response): Promise<ProviderOptionsMapResponse> => {
      const payload = (await response.json()) as {
        errorCode?: unknown;
        providerOptionsByIntentKey?: unknown;
        success?: boolean;
      };
      if (!response.ok || payload.success !== true) {
        providerOptionsRequestCache.delete(cacheKey);
        return {
          errorCode: normalizeCheckoutProviderOptionsErrorCode(
            payload.errorCode
          ),
          providerOptionsByIntentKey: EMPTY_PROVIDER_OPTIONS_MAP,
        };
      }

      return {
        errorCode: null,
        providerOptionsByIntentKey: parseProviderOptionsByIntentKey(
          payload.providerOptionsByIntentKey
        ),
      };
    })
    .catch((error) => {
      providerOptionsRequestCache.delete(cacheKey);
      throw error;
    });

  providerOptionsRequestCache.set(cacheKey, request);
  return request;
}

export function useSiteRuntimeCheckoutProviderOptionsMap(params: {
  checkoutIntentKeys: readonly string[];
  locale: string;
}) {
  const checkoutIntentKeySignature = normalizeCheckoutIntentKeys(
    params.checkoutIntentKeys
  ).join('|');
  const checkoutIntentKeys = checkoutIntentKeySignature
    ? checkoutIntentKeySignature.split('|')
    : [];
  const [state, setState] = useState<ProviderOptionsMapState>({
    errorCode: null,
    pending: checkoutIntentKeys.length > 0,
    providerOptionsByIntentKey: EMPTY_PROVIDER_OPTIONS_MAP,
  });

  useEffect(() => {
    let active = true;

    if (!checkoutIntentKeys.length) {
      setState({
        errorCode: null,
        pending: false,
        providerOptionsByIntentKey: EMPTY_PROVIDER_OPTIONS_MAP,
      });
      return;
    }

    setState({
      errorCode: null,
      pending: true,
      providerOptionsByIntentKey: EMPTY_PROVIDER_OPTIONS_MAP,
    });
    void loadProviderOptionsMap({
      checkoutIntentKeys,
      locale: params.locale,
    })
      .then((result) => {
        if (active) {
          setState({
            errorCode: result.errorCode,
            pending: false,
            providerOptionsByIntentKey: result.providerOptionsByIntentKey,
          });
        }
      })
      .catch(() => {
        if (active) {
          setState({
            errorCode: normalizeCheckoutProviderOptionsErrorCode(null),
            pending: false,
            providerOptionsByIntentKey: EMPTY_PROVIDER_OPTIONS_MAP,
          });
        }
      });

    return () => {
      active = false;
    };
  }, [checkoutIntentKeySignature, params.locale]);

  return {
    getProviderOptions: (checkoutIntentKey: string) =>
      state.pending
        ? null
        : (state.providerOptionsByIntentKey[checkoutIntentKey] ?? []),
    providerOptionsError: state.errorCode,
    providerOptionsPending: state.pending,
  };
}
